import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import { AnimatePresence, motion } from 'framer-motion'
import { MotionButton, MotionIcon, MotionLink } from '../motion/Motion'

const navItems = [
  'Home',
  'About Us',
  'Products',
  'Gallery',
  'Contact Us',
  'Ts & Cs',
]

const navHref: Record<string, string | null> = {
  Home: '/',
  'About Us': '/about',
  Products: '/products',
  Gallery: '/gallery',
  'Contact Us': '/contact',
  'Ts & Cs': '/terms-and-conditions-of-sale',
}

export default function Header() {
  const location = useLocation()
  const hideBottomLine =
    location.pathname === '/about' || location.pathname === '/contact' || location.pathname === '/products' || location.pathname === '/gallery'
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    if (!mobileOpen) return

    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow

    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow
      document.body.style.overflow = previousBodyOverflow
    }
  }, [mobileOpen])

  const isActiveHref = (href: string | null) => {
    if (!href) return false
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <header className={`sticky top-0 z-50 w-full bg-white ${hideBottomLine ? '' : 'shadow-sm'}`}>
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.button
              type="button"
              aria-label="Close menu"
              className="fixed inset-0 bg-black/50 z-[60] lg:hidden"
              onClick={() => setMobileOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />

            <motion.aside
              className="fixed top-0 left-0 h-full w-[82%] max-w-sm bg-white z-[70] flex flex-col lg:hidden"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="h-px w-full bg-gray-100" />

              <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100">
                <Link
                  to="/"
                  aria-label="Go to Home"
                  className="inline-flex items-center"
                  onClick={() => setMobileOpen(false)}
                >
                  <img
                    src="/images/logo/LOGO HEADER.png"
                    alt="Logo Branded Door Mats ZA"
                    className="h-12 w-auto object-contain"
                  />
                </Link>

                <button
                  type="button"
                  aria-label="Close menu"
                  className="p-2 rounded-md hover:bg-gray-100"
                  onClick={() => setMobileOpen(false)}
                >
                  <MotionIcon>
                    <CloseIcon fontSize="small" />
                  </MotionIcon>
                </button>
              </div>

              <nav className="px-6 py-6 flex-1 flex flex-col overflow-y-auto">
                <ul className="flex-1 flex flex-col justify-evenly text-sm font-medium text-gray-800">
                  {navItems.map((item) => {
                    const href = navHref[item] ?? null
                    const active = isActiveHref(href)

                    return (
                      <li key={item}>
                        {href ? (
                          <Link
                            to={href}
                            aria-current={active ? 'page' : undefined}
                            className={`block py-2 transition-colors ${
                              active ? 'text-pink-500 font-semibold' : 'hover:text-pink-500'
                            }`}
                            onClick={() => setMobileOpen(false)}
                          >
                            <MotionLink>{item}</MotionLink>
                          </Link>
                        ) : (
                          <span className="block py-2 text-gray-500">{item}</span>
                        )}
                      </li>
                    )
                  })}
                </ul>

                <div className="mt-auto pt-6 border-t border-gray-100">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center w-full rounded-full bg-pink-500 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-pink-600 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <MotionButton>Contact Us</MotionButton>
                  </Link>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="w-[96%] mx-auto flex items-center justify-between px-4 sm:px-6 py-4 gap-6 lg:px-6">
        <nav className="flex-1 overflow-x-visible hidden lg:block">
          <ul className="flex items-center gap-6 text-sm font-medium text-gray-800 whitespace-nowrap">
            {navItems.map((item) => {
              const href = navHref[item] ?? null
              const active = isActiveHref(href)

              return (
                <li key={item} className="cursor-pointer transition-colors">
                  {href ? (
                    <Link
                      to={href}
                      aria-current={active ? 'page' : undefined}
                      className={`${
                        active ? 'text-pink-500 font-semibold' : 'hover:text-pink-500'
                      } transition-colors`}
                    >
                      <MotionLink>{item}</MotionLink>
                    </Link>
                  ) : (
                    item
                  )}
                </li>
              )
            })}
          </ul>
        </nav>

        <div className="w-full flex items-center justify-between lg:w-auto lg:flex-1 lg:justify-center">
          <button
            type="button"
            aria-label="Open menu"
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-gray-100"
            onClick={() => setMobileOpen(true)}
          >
            <MotionIcon>
              <MenuIcon fontSize="small" />
            </MotionIcon>
          </button>

          <Link to="/" aria-label="Go to Home">
            <img
              src="/images/logo/LOGO HEADER.png"
              alt="Logo Branded Door Mats ZA"
              className="h-16 w-auto object-contain"
            />
          </Link>
        </div>

        <div className="flex-1 justify-end hidden lg:flex">
          <Link
            to="/contact"
            className="rounded-full bg-pink-500 px-6 py-2 text-sm font-semibold text-white shadow hover:bg-pink-600 transition-colors"
          >
            <MotionButton>Contact Us</MotionButton>
          </Link>
        </div>
      </div>
    </header>
  )
}
